import PersonAdd from "@mui/icons-material/PersonAdd";
import {
  Alert,
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { Reviewer } from "components/Submission/ReviewersEditList";
import { ReviewersDatagrid } from "components/Submission/ReviewersList";
import { Issue, ReviewMode, SubmissionEncampStatus } from "generated-graphql/graphql";
import { SubmissionFormData } from "hooks/useSubmissionForm";
import { DateTime } from "luxon";
import { UseFormSetValue } from "react-hook-form";

function PeerReviewToggle({
  disabled,
  peerReviewRequired,
  peerReviewUnclaimed,
  onPeerReviewToggle,
}: {
  disabled: boolean;
  peerReviewRequired: boolean;
  peerReviewUnclaimed: boolean;
  onPeerReviewToggle: (next: boolean) => void;
}) {
  return (
    <>
      {/* Matches the legacy form: peer review sits outside the review-required
          toggle, so it stays visible (and its own state) regardless of that choice. */}
      <FormControlLabel
        control={
          <Switch
            // `peerReviewRequired` means "is peer review needed" regardless
            // of who (if anyone) is assigned — naming a specific reviewer
            // doesn't clear it, so the switch reads the flag alone.
            checked={peerReviewRequired}
            onChange={(event) => onPeerReviewToggle(event.target.checked)}
            size="small"
            disabled={disabled}
          />
        }
        label={<Typography variant="body2">Require peer review</Typography>}
      />
      {/* Kept mounted so toggling the switch doesn't reflow the page. */}
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        visibility={peerReviewUnclaimed ? "visible" : "hidden"}
      >
        Open to any Encamp staff member. Add a specific peer reviewer to assign it.
      </Typography>
    </>
  );
}

function NoReviewRadioOption({ disabled }: { disabled: boolean }) {
  return (
    <FormControlLabel
      value="false"
      disabled={disabled}
      control={<Radio size="small" disabled={disabled} />}
      label={<Typography variant="body2">No review required</Typography>}
    />
  );
}

function ReviewerListControls({
  disabled,
  reviewRequired,
  onReviewRequiredChange,
  onAddReviewer,
  reviewers,
  onReviewersChange,
  reviewMode,
  setReviewMode,
  submissionDueDate,
  isStaff,
  submissionId,
  submissionEncampStatus,
  reviewRequiredIssues,
}: {
  disabled: boolean;
  reviewRequired: boolean;
  onReviewRequiredChange: (next: boolean) => void;
  onAddReviewer: () => void;
  reviewers: Reviewer[];
  onReviewersChange: (next: Reviewer[]) => void;
  reviewMode: ReviewMode;
  setReviewMode: UseFormSetValue<SubmissionFormData>;
  submissionDueDate: DateTime | undefined;
  isStaff: boolean;
  submissionId: string;
  submissionEncampStatus: SubmissionEncampStatus | null;
  reviewRequiredIssues: Issue[];
}) {
  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" fontWeight="medium">
          Reviewer(s)
        </Typography>
        <Tooltip title="Add reviewer">
          <span>
            <IconButton
              size="small"
              aria-label="Add reviewer"
              onClick={onAddReviewer}
              disabled={disabled || !reviewRequired}
              sx={{ color: !disabled && reviewRequired ? "primary.main" : "inherit" }}
            >
              <PersonAdd fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <RadioGroup
        row
        value={reviewRequired ? "true" : "false"}
        onChange={(e) => {
          if (disabled) return;
          onReviewRequiredChange(e.target.value === "true");
        }}
      >
        <NoReviewRadioOption disabled={disabled} />
        <FormControlLabel
          value="true"
          disabled={disabled}
          control={<Radio size="small" disabled={disabled} />}
          label={<Typography variant="body2">Include reviewers</Typography>}
        />
      </RadioGroup>
      {reviewRequired && (
        <Box>
          <ReviewersDatagrid
            reviewers={reviewers}
            setReviewers={disabled ? undefined : onReviewersChange}
            reviewMode={reviewMode}
            setValue={disabled ? undefined : setReviewMode}
            handleAddReviewer={onAddReviewer}
            submissionDueDate={submissionDueDate}
            disabled={disabled}
            isStaff={isStaff}
            submissionId={submissionId}
            submissionEncampStatus={submissionEncampStatus}
          />
          {reviewRequiredIssues.length > 0 && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {reviewRequiredIssues.map((i, index) => (
                <div key={index}>{i.message}</div>
              ))}
            </Alert>
          )}
        </Box>
      )}
    </>
  );
}

export function ReviewerConfigSection({
  disabled,
  reviewRequired,
  onReviewRequiredChange,
  onAddReviewer,
  peerReviewRequired,
  peerReviewUnclaimed,
  onPeerReviewToggle,
  reviewers,
  onReviewersChange,
  reviewMode,
  setReviewMode,
  submissionDueDate,
  isStaff,
  submissionId,
  submissionEncampStatus,
  reviewRequiredIssues,
}: {
  disabled: boolean;
  reviewRequired: boolean;
  onReviewRequiredChange: (next: boolean) => void;
  onAddReviewer: () => void;
  peerReviewRequired: boolean;
  peerReviewUnclaimed: boolean;
  onPeerReviewToggle: (next: boolean) => void;
  reviewers: Reviewer[];
  onReviewersChange: (next: Reviewer[]) => void;
  reviewMode: ReviewMode;
  setReviewMode: UseFormSetValue<SubmissionFormData>;
  submissionDueDate: DateTime | undefined;
  isStaff: boolean;
  submissionId: string;
  submissionEncampStatus: SubmissionEncampStatus | null;
  reviewRequiredIssues: Issue[];
}) {
  return (
    <Box>
      <PeerReviewToggle
        disabled={disabled}
        peerReviewRequired={peerReviewRequired}
        peerReviewUnclaimed={peerReviewUnclaimed}
        onPeerReviewToggle={onPeerReviewToggle}
      />
      <ReviewerListControls
        disabled={disabled}
        reviewRequired={reviewRequired}
        onReviewRequiredChange={onReviewRequiredChange}
        onAddReviewer={onAddReviewer}
        reviewers={reviewers}
        onReviewersChange={onReviewersChange}
        reviewMode={reviewMode}
        setReviewMode={setReviewMode}
        submissionDueDate={submissionDueDate}
        isStaff={isStaff}
        submissionId={submissionId}
        submissionEncampStatus={submissionEncampStatus}
        reviewRequiredIssues={reviewRequiredIssues}
      />
    </Box>
  );
}
